// Copyright 2018 Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Builds an LmiProposalRequest body from a small set of wizard options.
 * Gate 1 safe defaults verified against proposal-submission-service
 * gate01-validation.yaml and G1Validators.java.
 * Instruction used: scope-discipline.instructions.md
 */

import {
  definitionProperty,
  enumSelectOptions
} from './premium-quote-payload'

const ID_STORAGE_PREFIX = 'proposalSubmissionIds:'

// Instruction used: scope-discipline.instructions.md
export const PROPOSAL_SUBMISSION_WIZARD_ENABLED = true

export const DEFAULT_WIZARD_OPTIONS = {
  productName: 'Standard',
  loanSourceChannel: 'Retail',
  borrowerType: 'Individual',
  underwritingType: 'STD',
  includeGuarantor: false,
  loanPurpose: 'PCH',
  loanAmount: 480000,
  loanTermInMonths: 360,
  occupancyType: 'OwnerOccupied',
  securityType: 'HO',
  securityTypeStatus: 'E',
  valueKind: 'purchasePrice',
  securityValue: 600000,
  projectCosts: undefined,
  state: 'NSW',
  serviceabilityType: 'NDI',
  valuationMode: 'inPrinciple',
  valuationId: '',
  valuationDate: undefined,
  valuerCompanyName: 'Panel Valuer',
  funderId: undefined,
  originatorId: undefined,
  customerId: undefined,
  requestId: undefined
}

export function isProposalSubmissionApi(api) {
  const title = api && api.swagger && api.swagger.info && api.swagger.info.title
  // SIT catalog: ProposalSubmission-2-SIT. UAT catalog: LMI Proposal Submission API.
  // Exclude LIXI Proposal Submission titles.
  // Instruction used: scope-discipline.instructions.md
  return typeof title === 'string' && /proposal\s*submission/i.test(title) && !/lixi/i.test(title)
}

export function shouldShowProposalSubmissionWizard(api) {
  return PROPOSAL_SUBMISSION_WIZARD_ENABLED && isProposalSubmissionApi(api)
}

export function isLixiProposalSubmissionApi(api) {
  const title = api && api.swagger && api.swagger.info && api.swagger.info.title
  // SIT catalog: LixiProposalSubmission-SIT. UAT catalog: LMI Proposal Submission LIXI API.
  // Instruction used: scope-discipline.instructions.md
  return typeof title === 'string' && /proposal\s*submission/i.test(title) && /lixi/i.test(title)
}

export function shouldShowLixiProposalSubmissionWizard(api) {
  return PROPOSAL_SUBMISSION_WIZARD_ENABLED && isLixiProposalSubmissionApi(api)
}

export const FALLBACK_WIZARD_ENUMS = {
  productName: [
    { key: 'Standard', value: 'Standard', text: 'Standard' },
    { key: 'HomeBuyerPlus', value: 'HomeBuyerPlus', text: 'HomeBuyerPlus' },
    { key: 'BusinessSelect', value: 'BusinessSelect', text: 'BusinessSelect' },
    { key: 'FamilyPledge', value: 'FamilyPledge', text: 'FamilyPledge' }
  ],
  loanSourceChannel: [
    { key: 'Retail', value: 'Retail', text: 'Retail' },
    { key: 'ThirdParty', value: 'ThirdParty', text: 'Third party' },
    { key: 'Wholesale', value: 'Wholesale', text: 'Wholesale' },
    { key: 'Virtual', value: 'Virtual', text: 'Virtual' }
  ],
  loanPurpose: [
    { key: 'PCH', value: 'PCH', text: 'Purchase (PCH)' },
    { key: 'REF', value: 'REF', text: 'Refinance (REF)' },
    { key: 'DCO', value: 'DCO', text: 'Debt consolidation (DCO)' },
    { key: 'ERL', value: 'ERL', text: 'Equity release (ERL)' },
    { key: 'CFD', value: 'CFD', text: 'Controlled funds (CFD)' },
    { key: 'BRG', value: 'BRG', text: 'Bridging (BRG)' },
    { key: 'BCN', value: 'BCN', text: 'Construction (BCN)' },
    { key: 'HIM', value: 'HIM', text: 'Home improvements (HIM)' },
    { key: 'IRF', value: 'IRF', text: 'Internal refinance (IRF)' }
  ],
  occupancyType: [
    { key: 'OwnerOccupied', value: 'OwnerOccupied', text: 'Owner occupied' },
    { key: 'Investment', value: 'Investment', text: 'Investment' }
  ],
  securityType: [
    { key: 'HO', value: 'HO', text: 'House (HO)' },
    { key: 'UN', value: 'UN', text: 'Unit (UN)' },
    { key: 'CA', value: 'CA', text: 'Inner city apartment (CA)' },
    { key: 'SA', value: 'SA', text: 'Studio apartment (SA)' },
    { key: 'SM', value: 'SM', text: 'Serviced apartment (SM)' },
    { key: 'CO', value: 'CO', text: 'Converted commercial (CO)' },
    { key: 'CL', value: 'CL', text: 'Commercial (CL)' },
    { key: 'IN', value: 'IN', text: 'Industrial (IN)' },
    { key: 'VM', value: 'VM', text: 'Vacant land major (VM)' },
    { key: 'VO', value: 'VO', text: 'Vacant land other (VO)' },
    { key: 'GH', value: 'GH', text: 'Family Pledge house (GH)' },
    { key: 'GU', value: 'GU', text: 'Family Pledge unit (GU)' },
    { key: 'OT', value: 'OT', text: 'Other / rural (OT)' }
  ],
  securityTypeStatus: [
    { key: 'E', value: 'E', text: 'Established (E)' },
    { key: 'N', value: 'N', text: 'New (N)' },
    { key: 'V', value: 'V', text: 'Vacant land (V)' },
    { key: 'CH', value: 'CH', text: 'Construction H&L package (CH)' },
    { key: 'CX', value: 'CX', text: 'Construction off the plan (CX)' },
    { key: 'CO', value: 'CO', text: 'Construction owner builder (CO)' },
    { key: 'CS', value: 'CS', text: 'Construction building contract (CS)' }
  ],
  state: ['NSW', 'VIC', 'QLD', 'SA', 'ACT', 'TAS', 'NT', 'WA'].map(function (state) {
    return { key: state, value: state, text: state }
  }),
  borrowerType: [
    { key: 'Individual', value: 'Individual', text: 'Individual' },
    { key: 'Company', value: 'Company', text: 'Company' }
  ],
  serviceabilityType: [
    { key: 'NDI', value: 'NDI', text: 'Net Disposable Income (NDI)' },
    { key: 'NDS', value: 'NDS', text: 'Net Monthly Surplus (NDS)' },
    { key: 'DSR', value: 'DSR', text: 'Debt Servicing Ratio (DSR)' }
  ]
}

const WIZARD_ENUM_FIELDS = {
  productName: { definition: 'LmiProposalRequest', property: 'productName' },
  loanSourceChannel: { definition: 'LmiProposalRequest', property: 'loanSourceChannel' },
  loanPurpose: { definition: 'Loan', property: 'loanPurpose' },
  occupancyType: { definition: 'Security', property: 'occupancyType' },
  securityType: { definition: 'Security', property: 'securityType' },
  securityTypeStatus: { definition: 'Security', property: 'securityTypeStatus' },
  state: { definition: 'Address', property: 'state' },
  borrowerType: { definition: 'Applicant', property: 'applicantType' },
  serviceabilityType: { definition: 'Serviceability', property: 'lendersServiceabilityType' }
}

/**
 * Dropdown options from the selected API swagger enums.
 * Falls back to FALLBACK_WIZARD_ENUMS when a field is not enumerated.
 * Instruction used: scope-discipline.instructions.md
 */
export function wizardEnumOptions(api) {
  const swagger = api && api.swagger
  const options = {}
  Object.keys(WIZARD_ENUM_FIELDS).forEach(function (field) {
    const spec = WIZARD_ENUM_FIELDS[field]
    const property = definitionProperty(swagger, spec.definition, spec.property)
    options[field] = enumSelectOptions(
      property && property.enum,
      property && property.description,
      FALLBACK_WIZARD_ENUMS[field],
      field === 'securityType'
    )
  })
  // The swagger describes serviceability labels in lowercase
  // ("net disposable income ratio"); show them title-cased.
  options.serviceabilityType = options.serviceabilityType.map(function (option) {
    return Object.assign({}, option, { text: titleCaseWords(option.text) })
  })
  return options
}

function titleCaseWords(text) {
  return String(text).replace(/(^|\s|\()([a-z])/g, function (match, prefix, letter) {
    return prefix + letter.toUpperCase()
  })
}

export function loadPersistedProposalIds(username) {
  if (!username || typeof localStorage === 'undefined') return {}
  try {
    const raw = localStorage.getItem(ID_STORAGE_PREFIX + username)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return {
      funderId: toPositiveInt(parsed && parsed.funderId),
      originatorId: toPositiveInt(parsed && parsed.originatorId)
    }
  } catch (err) {
    return {}
  }
}

export function persistProposalIds(username, ids) {
  if (!username || typeof localStorage === 'undefined') return
  const funderId = toPositiveInt(ids && ids.funderId)
  const originatorId = toPositiveInt(ids && ids.originatorId)
  const stored = {}
  if (funderId != null) stored.funderId = funderId
  if (originatorId != null) stored.originatorId = originatorId
  localStorage.setItem(ID_STORAGE_PREFIX + username, JSON.stringify(stored))
}

export function generateProposalRequest(options) {
  const opts = Object.assign({}, DEFAULT_WIZARD_OPTIONS, options || {})
  const errors = []
  const funderId = toPositiveInt(opts.funderId)
  if (funderId == null) {
    errors.push('funderId is required')
  }
  const originatorId = toPositiveInt(opts.originatorId)
  // customerId must equal funderId or originatorId (E_FUNDER_ORIGINATOR);
  // default it to funderId.
  // Instruction used: scope-discipline.instructions.md
  let customerId = toPositiveInt(opts.customerId)
  if (customerId == null) customerId = funderId
  if (customerId != null && customerId !== funderId && customerId !== originatorId) {
    errors.push('customerId must equal funderId or originatorId')
  }

  const requestId = String(opts.requestId || ('portal-' + Date.now()))
  const payload = {
    requestId: requestId,
    productName: opts.productName || DEFAULT_WIZARD_OPTIONS.productName,
    loanSourceChannel: opts.loanSourceChannel || DEFAULT_WIZARD_OPTIONS.loanSourceChannel,
    // Fixed Gate 1 safe values: FullCover needs no coverRate, and new deals
    // must not send scheduledLoanBalance. DUA requires lenderLoanApprovalDate
    // (M_LOAN_APPROVAL_DATE); STD must not send it.
    // Instruction used: scope-discipline.instructions.md
    coverType: 'FullCover',
    underwritingType: opts.underwritingType || DEFAULT_WIZARD_OPTIONS.underwritingType,
    topUp: false,
    preInforceVariation: false,
    lenderReference: requestId.slice(0, 20),
    contactName: 'Portal User',
    contactPhone: '0290000000',
    applicants: buildApplicants(opts),
    loans: buildLoans(opts),
    securities: [buildSecurity(opts, errors)],
    serviceability: buildServiceability(opts)
  }

  if (funderId != null) payload.funderId = funderId
  if (originatorId != null) payload.originatorId = originatorId
  if (customerId != null) payload.customerId = customerId

  // Gate 1 (M_LOAN_APPROVAL_DATE): DUA proposals must carry the lender's loan
  // approval date; STD must not send it.
  // Instruction used: debugging.instructions.md
  if (payload.underwritingType === 'DUA') {
    payload.lenderLoanApprovalDate = todayDate()
  }

  return { payload, errors }
}

function buildApplicants(opts) {
  const state = opts.state || DEFAULT_WIZARD_OPTIONS.state
  const applicants = []
  if (opts.borrowerType === 'Company') {
    applicants.push({
      applicantType: 'Company',
      applicantRole: 'Borrower',
      primaryBorrower: true,
      company: {
        companyName: 'Sample Pty Ltd',
        companyType: 'P/L',
        // Digits only (E_COMPANY_ABN).
        companyABN: '51000000001',
        companyAddress: buildAddress(state)
      },
      income: { annualGrossIncome: 250000 }
    })
  } else {
    applicants.push({
      applicantType: 'Individual',
      applicantRole: 'Borrower',
      primaryBorrower: true,
      individual: buildIndividual('Borrower', '1985-06-15', state),
      income: { annualGrossIncome: 120000 }
    })
  }
  if (toBool(opts.includeGuarantor, false)) {
    applicants.push({
      applicantType: 'Individual',
      applicantRole: 'Guarantor',
      individual: buildIndividual('Guarantor', '1960-03-20', state),
      income: { annualGrossIncome: 90000 }
    })
  }
  return applicants
}

function buildIndividual(surname, dateOfBirth, state) {
  return {
    firstName: 'Sample',
    surname: surname,
    dateOfBirth: dateOfBirth,
    maritalStatus: 'Single',
    // DW001 is a warning, but a full valuation still reports it for each person.
    // Portal schema maxLength is 10. Bureau photo-ID number maxLength is 9.
    // Instruction used: debugging.instructions.md
    driversLicenseNumber: '123456789',
    permanentResident: true,
    employmentDetail: {
      currentEmploymentStatus: 'PAYE',
      employmentBasis: 'FullTime',
      industryType: 'PROF_SCI_TECH'
    },
    residentialAddress: buildAddress(state)
  }
}

function buildAddress(state) {
  return {
    streetNumber: '1',
    streetName: 'Sample',
    // Helia street type list uses full names (e.g. Street), not ST.
    // Instruction used: scope-discipline.instructions.md
    streetType: 'Street',
    suburb: 'Sydney',
    postcode: '2000',
    state: state,
    country: 'AU'
  }
}

function buildLoans(opts) {
  // loanType must agree with the security occupancyType: PC = Owner Occupied,
  // PI = Investment (SIT schema). There is no Gate 1 cross-check, so a
  // mismatch passes validation and submits as the wrong loan type downstream.
  // Instruction used: debugging.instructions.md
  const occupancyType = opts.occupancyType || DEFAULT_WIZARD_OPTIONS.occupancyType
  return [{
    loanType: occupancyType === 'Investment' ? 'PI' : 'PC',
    loanRepaymentType: 'PIF',
    initialInterestRateType: 'Variable',
    loanPurpose: opts.loanPurpose || DEFAULT_WIZARD_OPTIONS.loanPurpose,
    loanAmount: toPositiveNumber(opts.loanAmount, DEFAULT_WIZARD_OPTIONS.loanAmount),
    loanTermInMonths: toPositiveInt(opts.loanTermInMonths) || DEFAULT_WIZARD_OPTIONS.loanTermInMonths
  }]
}

// Construction statuses that make projectCosts mandatory (M_PROJECT_COST).
const CONSTRUCTION_STATUSES = ['CH', 'CX', 'CO', 'CS']

function buildSecurity(opts, errors) {
  const securityValue = toPositiveNumber(opts.securityValue, DEFAULT_WIZARD_OPTIONS.securityValue)
  const securityTypeStatus = opts.securityTypeStatus || DEFAULT_WIZARD_OPTIONS.securityTypeStatus
  const security = {
    occupancyType: opts.occupancyType || DEFAULT_WIZARD_OPTIONS.occupancyType,
    securityType: opts.securityType || DEFAULT_WIZARD_OPTIONS.securityType,
    securityTypeStatus: securityTypeStatus,
    securityAddress: buildAddress(opts.state || DEFAULT_WIZARD_OPTIONS.state)
  }

  // Emit purchasePrice or estimatedValue, never both (I_PURCHASE_OR_ESTIMATED).
  // Construction loans omit both: the SIT schema says purchasePrice must not
  // be supplied for a construction loan, estimatedValue applies only to
  // Established/Vacant Land, and projectCosts carries the construction cost.
  // Instruction used: debugging.instructions.md
  const isConstruction = CONSTRUCTION_STATUSES.indexOf(securityTypeStatus) !== -1
  if (!isConstruction) {
    if (opts.valueKind === 'estimatedValue') {
      security.estimatedValue = securityValue
    } else {
      security.purchasePrice = securityValue
    }
  }

  const projectCosts = toPositiveInt(opts.projectCosts)
  if (projectCosts != null) {
    security.projectCosts = projectCosts
  } else if (isConstruction) {
    errors.push('projectCosts is required for construction security status')
  }

  applyValuationMode(security, opts, errors, securityValue)
  return security
}

/**
 * Valuation program is Regular when valuationAmount > 0, In Principle when
 * absent. Regular needs valuationDetails unless Valex supplies a valuationId
 * (M_SECURITY_VAL_DETAILS).
 * Instruction used: scope-discipline.instructions.md
 */
function applyValuationMode(security, opts, errors, securityValue) {
  const mode = opts.valuationMode || DEFAULT_WIZARD_OPTIONS.valuationMode
  if (mode === 'valex') {
    security.valuationAmount = securityValue
    security.valuationPlatform = 'Valex'
    const valuationId = String(opts.valuationId || '').trim()
    if (!valuationId) {
      errors.push('valuationId is required for the Valex shortcut')
    } else {
      security.valuationId = valuationId
    }
    return
  }
  if (mode === 'full') {
    security.valuationAmount = securityValue
    // All fields below are required for a non-AVM, non-Desktop valuation
    // (M_CURRENT_USE, M_MARKET, M_ESS_REPAIRS, M_INT_COND, M_EXT_COND).
    // valuerCompanyName is not a Gate 1 field, but the downstream LIXI policy
    // system rejects a full valuation without a valuation number or valuer
    // name (M_VAL_NUMBER); there is no valuationNumber field in the API
    // schema, so the valuer company name is the only way to satisfy it.
    // Instruction used: debugging.instructions.md
    security.valuationDetails = {
      valuationType: 'Full',
      valuationDate: opts.valuationDate || todayDate(),
      valuerCompanyName: (opts.valuerCompanyName || DEFAULT_WIZARD_OPTIONS.valuerCompanyName).trim(),
      valuerFirstName: 'Sample',
      valuerSurname: 'Valuer',
      currentUse: 'Residential',
      marketability: 'Good',
      essentialRepairsRequired: false,
      internalCondition: 'Good',
      externalCondition: 'Good',
      // Downstream full-valuation rules, not Gate 1: site area and its unit,
      // bedrooms, and all eight property/market risk ratings.
      // Instruction used: debugging.instructions.md
      siteArea: 600,
      siteAreaUnitOfMeasurement: 'SQUAREMETERS',
      noOfBedrooms: 3,
      propertyAndMarketRiskRatings: {
        locationAndNeighbourhood: 'Low',
        land: 'Low',
        environmentalIssues: 'Low',
        improvements: 'Low',
        marketVolatality: 'Low',
        recentMarketDirection: 'Low',
        localEconomyImpact: 'Low',
        marketSegmentConditions: 'Low'
      }
    }
  }
}

function buildServiceability(opts) {
  const type = opts.serviceabilityType || DEFAULT_WIZARD_OPTIONS.serviceabilityType
  const serviceability = { lendersServiceabilityType: type }
  // Each type has one mandatory companion field
  // (M_SECURITY_NDI / M_SECURITY_NDS / M_SECURITY_DSR).
  if (type === 'NDS') {
    serviceability.lendersNetMonthlySurplus = 500
  } else if (type === 'DSR') {
    serviceability.lendersDebtServicingRatio = 30
  } else {
    serviceability.lendersServiceabilityNdiRatio = 1.5
  }
  return serviceability
}

function todayDate() {
  return new Date().toISOString().slice(0, 10)
}

function toBool(value, defaultValue) {
  if (value === true || value === 'true') return true
  if (value === false || value === 'false') return false
  return defaultValue
}

function toPositiveInt(value) {
  if (value === '' || value == null) return undefined
  const n = Number(value)
  if (!Number.isInteger(n) || n <= 0) return undefined
  return n
}

function toPositiveNumber(value, fallback) {
  if (value === '' || value == null) return fallback
  const n = Number(value)
  if (!Number.isFinite(n) || n <= 0) return fallback
  return n
}

export { toPositiveInt, toPositiveNumber, todayDate }
